import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    MenuItem,
} from "@mui/material";
import axios from "axios";

const WeeklyTimecardPage = () => {
    const [sites, setSites] = useState([]);
    const [selectedSite, setSelectedSite] = useState("");
    const [selectedMonth, setSelectedMonth] = useState(
        new Date().getMonth() + 1
    ); // 1–12
    const [weeks, setWeeks] = useState([]);
    const [selectedWeekStart, setSelectedWeekStart] = useState("");
    const [datesOfWeek, setDatesOfWeek] = useState([]); // Array of Date objects (Sun → Sat)
    const [employees, setEmployees] = useState([]);
    const [timeEntries, setTimeEntries] = useState({}); // { [empApiid]: { [YYYY-MM-DD]: "hours" } }
    const [error, setError] = useState("");

    const currentYear = new Date().getFullYear();

    // Fetch all sites (SchoolMst) on mount
    useEffect(() => {
        axios
            .get("https://figazuresql.swancreate.com/api/swa/SchoolMst")
            .then((res) => {
                // res.data is expected to be an array of { SchoolId, SchoolName, ... }
                setSites(res.data);
                if (res.data.length > 0) {
                    setSelectedSite(res.data[0].SchoolId);
                }
            })
            .catch((err) => {
                console.error("Error fetching sites:", err);
                setError("Unable to load sites.");
            });
    }, []);

    // When selectedMonth changes, compute all Sundays (week starts) in that month
    useEffect(() => {
        const monthIndex = selectedMonth - 1; // zero-based
        const firstOfMonth = new Date(currentYear, monthIndex, 1);

        // Find all Sundays in the chosen month
        const sundays = [];
        const dayOfWeek = firstOfMonth.getDay(); // 0 = Sunday
        let firstSunday =
            dayOfWeek === 0
                ? new Date(firstOfMonth)
                : new Date(
                    currentYear,
                    monthIndex,
                    1 + (7 - dayOfWeek)
                );
        // If that Sunday is in the previous month, move to the first Sunday ≥ day 1
        if (firstSunday.getMonth() !== monthIndex) {
            firstSunday = new Date(
                currentYear,
                monthIndex,
                1 + ((7 - dayOfWeek) % 7)
            );
        }

        let cursor = new Date(firstSunday);
        while (cursor.getMonth() === monthIndex) {
            sundays.push(new Date(cursor));
            cursor.setDate(cursor.getDate() + 7);
        }

        const weekOptions = sundays.map((sun) => {
            const label = `Week of ${sun.toLocaleDateString("en-US", {
                month: "numeric",
                day: "numeric",
            })}`;
            const iso = sun.toISOString().split("T")[0];
            return { label, iso };
        });

        setWeeks(weekOptions);
        setSelectedWeekStart("");
        setDatesOfWeek([]);
        setEmployees([]);
        setTimeEntries({});
    }, [selectedMonth, currentYear]);

    // When a week is picked, compute the dates (Sun→Sat) and fetch employees for that site
    useEffect(() => {
        if (!selectedWeekStart) return;

        const sunday = new Date(selectedWeekStart);
        const weekDates = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date(sunday);
            d.setDate(sunday.getDate() + i);
            weekDates.push(d);
        }
        setDatesOfWeek(weekDates);

        // Fetch all employees from EmployeeTimecard, then filter by SiteId
        axios
            .get("https://figazuresql.swancreate.com/api/swa/EmployeeTimecard")
            .then((res) => {
                // res.data: array of { apiid, EmployeeName, EmployeeNumber, SiteId, SiteName, BaseRate, ... }
                const filtered = res.data.filter((u) => u.SiteId === selectedSite);
                setEmployees(filtered);

                // Initialize timeEntries: for each emp and each date, set ""
                const initialEntries = {};
                filtered.forEach((emp) => {
                    initialEntries[emp.apiid] = {};
                    weekDates.forEach((dt) => {
                        const key = dt.toISOString().split("T")[0]; // "YYYY-MM-DD"
                        initialEntries[emp.apiid][key] = "";
                    });
                });
                setTimeEntries(initialEntries);
            })
            .catch((err) => {
                console.error("Error fetching employees:", err);
                setError("Unable to load employees.");
            });
    }, [selectedWeekStart, selectedSite]);

    const handleHoursChange = (empId, dateKey, value) => {
        // Allow only numbers and dot, up to 2 decimals
        const sanitized = value.replace(/[^0-9.]/g, "");
        if (sanitized.match(/^\d*\.?\d{0,2}$/)) {
            setTimeEntries((prev) => ({
                ...prev,
                [empId]: {
                    ...prev[empId],
                    [dateKey]: sanitized,
                },
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        const payloads = [];

        employees.forEach((emp) => {
            const entries = timeEntries[emp.apiid];
            Object.entries(entries).forEach(([dateKey, hoursStr]) => {
                const hours = parseFloat(hoursStr);
                if (!isNaN(hours) && hours > 0) {
                    const [year, month, day] = dateKey.split("-").map(Number);
                    const clockIn = new Date(year, month - 1, day, 8, 0, 0);
                    const clockOut = new Date(clockIn);
                    clockOut.setMinutes(clockIn.getMinutes() + hours * 60);

                    payloads.push({
                        UserID: emp.apiid,
                        FirstName: emp.EmployeeName.split(" ")[0] || emp.EmployeeName,
                        LastName:
                            emp.EmployeeName.split(" ").slice(1).join(" ") || emp.EmployeeName,
                        SiteID: emp.SiteId,
                        SiteName: emp.SiteName,
                        ClockInTime: clockIn.toISOString(),
                        ClockOutTime: clockOut.toISOString(),
                        TimeWorked: hours.toFixed(2),
                        HourlyRate: emp.BaseRate || 0,       // Include BaseRate as HourlyRate
                        EmployeeName: emp.EmployeeName,
                        EmployeeNumber: emp.EmployeeNumber,
                    });
                }
            });
        });

        if (payloads.length === 0) {
            setError("No hours entered. Please fill at least one field.");
            return;
        }

        try {
            // Post each entry individually (or adapt to a batch endpoint if available)
            for (let i = 0; i < payloads.length; i++) {
                await axios.post(
                    "https://figazuresql.swancreate.com/api/swa/TimeCard",
                    payloads[i]
                );
            }
            alert("Timecards saved successfully.");
            // Optionally, reset the form
            setSelectedWeekStart("");
            setDatesOfWeek([]);
            setEmployees([]);
            setTimeEntries({});
        } catch (err) {
            console.error("Error saving timecards:", err);
            setError("Failed to save some or all timecards.");
        }
    };

    return (
        <Box
            sx={{
                backgroundColor: "#1A1A2E",
                color: "#EFFFFD",
                minHeight: "100vh",
                p: 4,
            }}
        >
            <Typography
                variant="h3"
                sx={{ mb: 3, fontWeight: "bold", textAlign: "center" }}
            >
                Weekly Timecard Entry
            </Typography>

            {/* Site Selection */}
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                <TextField
                    select
                    label="Select Site"
                    value={selectedSite}
                    onChange={(e) => setSelectedSite(e.target.value)}
                    sx={{
                        backgroundColor: "#0D1B2A",
                        borderRadius: "8px",
                        width: 200,
                        "& .MuiInputLabel-root": { color: "#EFFFFD" },
                        "& .MuiOutlinedInput-root": {
                            color: "#EFFFFD",
                            fontSize: "16px",
                        },
                    }}
                >
                    {sites.map((site) => (
                        <MenuItem key={site.SchoolId} value={site.SchoolId}>
                            {site.SchoolName}
                        </MenuItem>
                    ))}
                </TextField>

                {/* Month Selection */}
                <TextField
                    select
                    label="Select Month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    sx={{
                        backgroundColor: "#0D1B2A",
                        borderRadius: "8px",
                        width: 200,
                        "& .MuiInputLabel-root": { color: "#EFFFFD" },
                        "& .MuiOutlinedInput-root": {
                            color: "#EFFFFD",
                            fontSize: "16px",
                        },
                    }}
                >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                        <MenuItem key={m} value={m}>
                            {new Date(currentYear, m - 1).toLocaleString("en-US", {
                                month: "long",
                            })}
                        </MenuItem>
                    ))}
                </TextField>

                {/* Week Selection */}
                <TextField
                    select
                    label="Select Week"
                    value={selectedWeekStart}
                    onChange={(e) => setSelectedWeekStart(e.target.value)}
                    sx={{
                        backgroundColor: "#0D1B2A",
                        borderRadius: "8px",
                        width: 250,
                        "& .MuiInputLabel-root": { color: "#EFFFFD" },
                        "& .MuiOutlinedInput-root": {
                            color: "#EFFFFD",
                            fontSize: "16px",
                        },
                    }}
                >
                    <MenuItem value="">-- Choose Week --</MenuItem>
                    {weeks.map((w) => (
                        <MenuItem key={w.iso} value={w.iso}>
                            {w.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Box>

            {error && (
                <Typography color="error" sx={{ mb: 2, textAlign: "center" }}>
                    {error}
                </Typography>
            )}

            {/* Timecard Grid */}
            {datesOfWeek.length > 0 && employees.length > 0 && (
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{
                        overflowX: "auto",
                        pt: 3,
                    }}
                >
                    <Box
                        component="table"
                        sx={{
                            width: "100%",
                            borderCollapse: "collapse",
                            minWidth: 800,
                        }}
                    >
                        <Box component="thead">
                            <Box
                                component="tr"
                                sx={{
                                    backgroundColor: "#16213E",
                                    "& th": {
                                        color: "#EFFFFD",
                                        border: "1px solid #EFFFFD",
                                        p: 1,
                                        whiteSpace: "nowrap",
                                    },
                                }}
                            >
                                <Box component="th">Employee Name</Box>
                                <Box component="th">Employee Number</Box>
                                {datesOfWeek.map((dt) => (
                                    <Box key={dt.toISOString()} component="th">
                                        {dt.toLocaleDateString("en-US", {
                                            weekday: "short",
                                            month: "numeric",
                                            day: "numeric",
                                        })}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {employees.map((emp, idx) => (
                                <Box
                                    component="tr"
                                    key={emp.apiid}
                                    sx={{
                                        backgroundColor:
                                            idx % 2 === 0 ? "#0D1B2A" : "#112240",
                                        "& td": {
                                            border: "1px solid #EFFFFD",
                                            p: 1,
                                        },
                                    }}
                                >
                                    <Box component="td">{emp.EmployeeName}</Box>
                                    <Box component="td">{emp.EmployeeNumber}</Box>
                                    {datesOfWeek.map((dt) => {
                                        const dateKey = dt.toISOString().split("T")[0];
                                        return (
                                            <Box component="td" key={dateKey}>
                                                <TextField
                                                    type="number"
                                                    value={timeEntries[emp.apiid][dateKey]}
                                                    onChange={(e) =>
                                                        handleHoursChange(
                                                            emp.apiid,
                                                            dateKey,
                                                            e.target.value
                                                        )
                                                    }
                                                    inputProps={{
                                                        step: 0.01,
                                                        min: 0,
                                                        style: {
                                                            color: "#EFFFFD",
                                                            fontSize: "14px",
                                                            textAlign: "center",
                                                            MozAppearance: "textfield",
                                                        },
                                                    }}
                                                    sx={{
                                                        width: "70px",
                                                        "& .MuiOutlinedInput-root": {
                                                            backgroundColor: "#1A2634",
                                                            borderRadius: "4px",
                                                        },
                                                        "& .MuiOutlinedInput-notchedOutline": {
                                                            borderColor: "#EFFFFD",
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        );
                                    })}
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    <Box sx={{ textAlign: "center", mt: 3 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            sx={{
                                backgroundColor: "#1E90FF",
                                color: "#121212",
                                fontSize: "18px",
                                py: 1.5,
                                px: 3,
                                borderRadius: "8px",
                            }}
                        >
                            Save Timecards
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default WeeklyTimecardPage;
